import React, { useCallback, useEffect, useState } from 'react';
import type { VoiceCallDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { ask } from '../dialogs';
import { ErrorBanner } from '../ui';

/**
 * Chiamate: ogni chiamata all'agente telefonico, con categoria, riassunto, testo completo e registrazione.
 */

const CATEGORY: Record<string, string> = {
  informazioni: 'Informazioni',
  commerciale: 'Vuole un preventivo',
  supporto: 'Ha un problema',
  sicurezza: 'Sicurezza',
  prova: 'Prova',
  altro: 'Altro',
};

const when = (iso: string) =>
  new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const length = (s: number) => (s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`);

function Detail({ ctx, id, onDeleted }: { ctx: Ctx; id: number; onDeleted: () => void }) {
  const [call, setCall] = useState<VoiceCallDto | null>(null);
  const [audio, setAudio] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let url: string | null = null;
    let alive = true;
    setCall(null);
    setAudio(null);
    ctx.api.get<VoiceCallDto>(`/api/office/calls/${id}`).then(
      (c) => {
        if (!alive) return;
        setCall(c);
        if (c.hasAudio)
          ctx.api
            .download(`/api/office/calls/${id}/audio`)
            .then((b) => {
              if (!alive) return;
              url = URL.createObjectURL(b);
              setAudio(url);
            })
            .catch(() => {});
      },
      (e) => alive && setError((e as Error).message),
    );
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [ctx.api, id]);

  const remove = async () => {
    if (!(await ask('Cancellare questa chiamata e la sua registrazione? Non si può annullare.'))) return;
    try {
      await ctx.api.del(`/api/office/calls/${id}`);
      onDeleted();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (!call) return <section className="panel vc-detail">{error ? <ErrorBanner message={error} onClose={() => setError(null)} /> : <p className="muted">Carico…</p>}</section>;
  return (
    <section className="panel vc-detail">
      <header className="panel-head">
        <div className="grow">
          <h2>{CATEGORY[call.category] ?? call.category}</h2>
          <div className="subtitle">
            {when(call.startedAt)} · {length(call.durationS)}
            {call.caller ? ` · ${call.caller}` : ''}
          </div>
        </div>
        <button type="button" className="btn small" onClick={remove} title="Cancella la chiamata e la registrazione">
          Cancella
        </button>
      </header>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {call.summary && <p className="vc-summary">{call.summary}</p>}
      {call.hasAudio && (audio ? <audio controls src={audio} className="vc-audio" /> : <p className="muted small">Carico la registrazione…</p>)}
      <div className="vc-chat">
        {(call.lines ?? []).map((l, i) => (
          <div key={i} className={`vc-say ${l.who}`}>
            <b>{l.who === 'tu' ? 'Chi chiama' : 'Agente'}</b>
            {l.text}
          </div>
        ))}
      </div>
    </section>
  );
}

export function CallsPage({ ctx }: { ctx: Ctx }) {
  const [list, setList] = useState<VoiceCallDto[] | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    ctx.api.get<VoiceCallDto[]>('/api/office/calls').then(
      (r) => {
        setList(r);
        setSel((cur) => (cur && r.some((c) => c.id === cur) ? cur : (r[0]?.id ?? null)));
      },
      (e) => setError((e as Error).message),
    );
  }, [ctx.api]);
  useEffect(load, [load]);

  return (
    <div className="vc-page">
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <section className="panel vc-list">
        <header className="panel-head">
          <div className="grow">
            <h2>Chiamate</h2>
            <div className="subtitle">Ogni chiamata all'agente, con testo e registrazione.</div>
          </div>
        </header>
        {list && list.length === 0 && <p className="muted">Nessuna chiamata ancora. Appena ne arriva una, la trovi qui.</p>}
        {(list ?? []).map((c) => (
          <button key={c.id} type="button" className={`vc-row${c.id === sel ? ' on' : ''}`} onClick={() => setSel(c.id)}>
            <span className="vc-cat">{CATEGORY[c.category] ?? c.category}</span>
            <span className="vc-when">
              {when(c.startedAt)} · {length(c.durationS)}
            </span>
            <span className="vc-sum">{c.summary ?? '—'}</span>
          </button>
        ))}
      </section>
      {sel ? <Detail ctx={ctx} id={sel} onDeleted={load} /> : <section className="panel vc-detail" />}
    </div>
  );
}
